import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "../components/ui/use-toast";

// Create a dedicated axios instance with the correct base URL
const scheduleAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://isco-backend-deployment-932661235642.us-central1.run.app",
  headers: {
    "Content-Type": "application/json"
  }
});

/**
 * Custom hook for managing RSM assigned schedules
 * @returns {Object} Schedules state and functions
 */
export default function useRsmSchedules() {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  const [userData, setUserData] = useState(null);

  // Fetch user data and profile
  const fetchUserData = useCallback(async () => {
    try {
      console.log("Fetching user data...");
      const response = await scheduleAxios.get("/auth/current-user");
      if (response.data) {
        console.log("User data received:", response.data.username);
        setUserData(response.data);
        return response.data;
      }
      return null;
    } catch (err) {
      console.error("Error fetching user data:", err);
      return null;
    }
  }, []);

  // Fetch all assigned schedules
  const fetchAssignedSchedules = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Make sure the auth header is set in scheduleAxios defaults
      const token = localStorage.getItem("isco_jwt");
      if (token) {
        scheduleAxios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      }

      console.log("Fetching assigned schedules...");
      const response = await scheduleAxios.get("/schedules/"); // Added trailing slash
      console.log(
        "Schedules response:",
        response.status,
        response.data?.length || 0,
      );

      if (Array.isArray(response.data) && response.data.length > 0) {
        // Debug: Log the first schedule to see available fields
        console.log("RSM Schedule fields:", Object.keys(response.data[0]));
        console.log("RSM Schedule data sample:", response.data[0]);
        
        setSchedules(response.data);
        // Automatically select the first schedule if none selected
        if (!selectedScheduleId && response.data.length > 0) {
          setSelectedScheduleId(response.data[0].id || response.data[0].cluster);
        }
        return response.data;
      } else if (Array.isArray(response.data)) {
        // Empty array is a valid response (no schedules)
        setSchedules([]);
        setSelectedScheduleId(null);
        console.log("No schedules found for RSM");
        return [];
      } else {
        // Invalid response format
        console.error("Invalid response format from /schedules", response.data);
        setSchedules([]);
        setError("Invalid response format from server.");
        return [];
      }
    } catch (err) {
      console.error("Error fetching assigned schedules:", err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch assigned schedules.";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [selectedScheduleId]); // Added selectedScheduleId as dependency

  // Fetch a specific schedule by ID
  const fetchScheduleById = useCallback(async (scheduleId) => {
    if (!scheduleId) return null;

    setLoading(true);
    try {
      console.log(`Fetching schedule ${scheduleId}...`);
      const response = await scheduleAxios.get(`/schedules/${scheduleId}`);

      if (response.data) {
        console.log(`Schedule ${scheduleId} data received`);
        // Update the schedule in the schedules array if it exists
        setSchedules((prevSchedules) => {
          const index = prevSchedules.findIndex(
            (s) => (s.id || s.cluster) === scheduleId,
          );
          if (index !== -1) {
            const updatedSchedules = [...prevSchedules];
            updatedSchedules[index] = response.data;
            return updatedSchedules;
          }
          // If not found, add it (might happen on direct load)
          return [...prevSchedules, response.data];
        });
        return response.data;
      }
      return null;
    } catch (err) {
      console.error(`Error fetching schedule ${scheduleId}:`, err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        `Failed to fetch schedule ${scheduleId}.`;
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, []); // Removed setLoading dependency, handled internally

  // Update stop progress
  const updateStopProgress = useCallback(
    async (stopId, status, notes = "") => {
      // *** ADDED LOGGING AND VALIDATION ***
      console.log(
        `[updateStopProgress] Attempting update. Stop ID: ${stopId}, Status: ${status}`,
      );
      if (!stopId) {
        console.error(
          "[updateStopProgress] Error: stopId is missing or invalid.",
        );
        toast({
          title: "Update Error",
          description: "Cannot update stop: Missing identifier.",
          variant: "destructive",
        });
        return false; // Prevent API call with invalid ID
      }
      // *** END LOGGING AND VALIDATION ***

      try {
        const response = await scheduleAxios.put(`/progress/stop/${stopId}`, {
          status,
          notes,
        });

        if (response.data) {
          console.log(`Stop ${stopId} status updated successfully via API.`);
          // Refresh the schedules after updating a stop
          // Consider fetching only the affected schedule for performance if needed:
          // await fetchScheduleById(selectedScheduleId);
          await fetchAssignedSchedules(); // Fetches all for simplicity now
          toast({
            title: "Success",
            description: `Stop status updated to ${status}`,
          });
          return true;
        }
        // If response.data is falsy but no error (e.g., 204 No Content)
        console.warn(
          `[updateStopProgress] Update API call successful but no data returned for stop ${stopId}. Status: ${response.status}`,
        );
        await fetchAssignedSchedules(); // Still refresh on success
        toast({
          title: "Success",
          description: `Stop status updated to ${status}`,
        });
        return true;
      } catch (err) {
        console.error(`Error updating stop ${stopId}:`, err);

        // *** ADDED SPECIFIC 404 HANDLING ***
        if (err.response && err.response.status === 404) {
          console.error(
            `[updateStopProgress] Received 404 for stop ID: ${stopId}. Stop not found on backend.`,
          );
          toast({
            title: "Update Failed",
            description: `Stop with ID ${stopId} could not be found. It might have been removed. Please refresh.`,
            variant: "destructive",
          });
        } else {
          // Generic error handling
          const errorMessage =
            err.response?.data?.error ||
            err.message ||
            `Failed to update stop status.`;
          toast({
            title: "Update Error",
            description: errorMessage,
            variant: "destructive",
          });
        }
        // *** END SPECIFIC 404 HANDLING ***
        return false;
      }
    },
    [fetchAssignedSchedules],
  ); // fetchAssignedSchedules is the correct dependency here

  // Load user data and schedules on mount
  useEffect(() => {
    // Make sure the auth header is set in scheduleAxios defaults
    const token = localStorage.getItem("isco_jwt");
    if (token) {
      console.log("Token found, setting Authorization header");
      scheduleAxios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      console.warn("No auth token found for RSM");
      setError("Authentication token not found. Please log in again.");
      setLoading(false);
      return; // Stop loading if no token
    }

    // Fetch user data first, then schedules
    const loadData = async () => {
      setLoading(true); // Ensure loading is true at the start
      try {
        await fetchUserData();
        await fetchAssignedSchedules();
      } catch (e) {
        console.error("Error in RSM data loading sequence:", e);
        // Error is likely set within fetchAssignedSchedules, but set a generic one if not
        if (!error) {
          setError("Failed to load initial RSM data. Please try refreshing.");
        }
      } finally {
        // setLoading(false); // setLoading is handled within fetchAssignedSchedules
      }
    };

    loadData();
  }, [fetchUserData, fetchAssignedSchedules]); // Dependencies are correct

  // Get the selected schedule from state
  const selectedSchedule = selectedScheduleId
    ? schedules.find((s) => (s.id || s.cluster) === selectedScheduleId)
    : null;

  return {
    schedules,
    loading,
    error,
    selectedScheduleId,
    selectedSchedule,
    userData,
    setSelectedScheduleId,
    fetchAssignedSchedules, // Keep exposing if needed elsewhere
    fetchScheduleById, // Keep exposing if needed elsewhere
    updateStopProgress,
  };
}
